import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Code, Database, Cloud, Globe, Brain, Zap } from 'lucide-react';

const Skills: React.FC = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const skillCategories = [
    {
      name: "Programming Languages",
      icon: <Code size={24} />,
      skills: ["Python", "JavaScript", "C++", "HTML", "CSS"]
    },
    {
      name: "Frontend Technologies",
      icon: <Globe size={24} />,
      skills: ["React", "Next.js", "Tailwind CSS", "Bootstrap"]
    },
    {
      name: "Backend & Frameworks",
      icon: <Code size={24} />,
      skills: ["Django", "Flask", "FastAPI", "Node.js", "Express.js"]
    },
    {
      name: "Databases",
      icon: <Database size={24} />,
      skills: ["MySQL", "SQLite"]
    },
    {
      name: "Cloud & DevOps",
      icon: <Cloud size={24} />,
      skills: ["AWS EC2", "AWS Lambda", "Nginx", "Gunicorn"]
    },
    {
      name: "AI & Machine Learning",
      icon: <Brain size={24} />,
      skills: ["PyTorch", "torchvision", "Machine Learning", "Deep Learning", "Convolutional Neural Networks", "Transfer Learning", "Model Fine-Tuning", "Data Augmentation", "Sentiment Analysis", "NLP Pipelines", "Model Evaluation"]
    }
  ];

  return (
    <section id="skills" className="py-20 bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl font-bold text-white mb-4">Technical Skills</h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            A comprehensive overview of my technical expertise across various domains and technologies.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 xl:grid-cols-3 gap-8">
          {skillCategories.map((category, categoryIndex) => (
            <motion.div
              key={categoryIndex}
              initial={{ opacity: 0, y: 50 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: categoryIndex * 0.1 }}
              className="bg-gray-800 rounded-2xl p-6 shadow-xl border border-gray-700 hover:border-blue-500/50 transition-all duration-300"
            >
              {/* Category Header */}
              <div className="flex items-center mb-6">
                <div className="bg-blue-600 p-3 rounded-full mr-4">
                  {category.icon}
                </div>
                <h3 className="text-xl font-bold text-white">{category.name}</h3>
              </div>

              {/* Skills List */}
              <div className="flex flex-wrap gap-2">
                {category.skills.map((skill, skillIndex) => (
                  <motion.span
                    key={skillIndex}
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={inView ? { opacity: 1, scale: 1 } : {}}
                    transition={{ duration: 0.5, delay: categoryIndex * 0.1 + skillIndex * 0.1 }}
                    className="bg-gray-600 text-gray-300 px-3 py-2 rounded-lg text-sm font-medium border border-gray-500 hover:border-blue-500 hover:text-blue-400 transition-all duration-200"
                  >
                    {skill}
                  </motion.span>
                ))}
              </div>
            </motion.div>
          ))}
        </div>

        {/* Additional Skills */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.8 }}
          className="mt-16 text-center"
        >
          <h3 className="text-2xl font-bold text-white mb-8">Additional Expertise</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto">
            {[
              "Full-Stack Development",
              "REST APIs",
              "Cloud Computing",
              "Database Design",
              "Problem Solving",
              "Performance Optimization",
              "Model Evaluation",
              "NLP Pipelines"
            ].map((skill, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={inView ? { opacity: 1, scale: 1 } : {}}
                transition={{ duration: 0.5, delay: 1 + index * 0.1 }}
                className="bg-gray-700 text-gray-300 px-4 py-3 rounded-xl text-sm font-medium border border-gray-600 hover:border-blue-500 hover:text-blue-400 transition-all duration-200"
              >
                {skill}
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Skills Summary */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 1.2 }}
          className="mt-16 text-center"
        >
          <div className="bg-gradient-to-r from-blue-600/20 to-purple-600/20 rounded-2xl p-8 border border-blue-500/30">
            <div className="flex items-center justify-center mb-4">
              <Zap className="text-blue-400 mr-3" size={32} />
              <h3 className="text-2xl font-bold text-white">Continuous Learning</h3>
            </div>
            <p className="text-gray-300 text-lg max-w-3xl mx-auto">
              I'm constantly expanding my skill set and staying updated with the latest technologies. 
              My passion for learning drives me to explore new domains and improve existing capabilities.
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Skills; 